import { useEffect, useState, type FormEvent } from 'react';
import type { State } from '../shared/model';
import type { CollectionPlan } from '../shared/campaigns';
import { CollectionHistory } from './CollectionHistory';
import { EmailInvites } from './EmailInvites';
const labels = {
  admin: 'Administrador',
  editor: 'Editor',
  approver: 'Aprovador',
  reader: 'Leitor',
};
async function send(data: State, path: string, body: unknown, method = 'POST') {
  const r = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', 'X-FLG-CSRF': data.csrf },
    body: JSON.stringify(body),
  });
  const result = await r.json();
  if (!r.ok) throw new Error(result.error ?? 'Não foi possível salvar.');
  return result;
}
export function TeamSettings({ data, refresh }: { data: State; refresh: () => Promise<unknown> }) {
  const [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [invite, setInvite] = useState('');
  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel team-settings">
      <h2>Equipe e permissões</h2>
      <p>
        Editor prepara. Aprovador decide nas campanhas designadas. Administrador gerencia a equipe e
        pode aprovar. Leitor consulta.
      </p>
      {error && (
        <p className="notice error" role="alert">
          {error}
        </p>
      )}
      {data.mode === 'local' && (
        <p className="notice">
          Operador local com acesso administrativo. No domínio publicado, os papéis são vinculados
          ao login individual.
        </p>
      )}
      {data.members.map((m) => (
        <form
          key={`${m.id}:${m.version}`}
          className="team-member"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void run(() =>
              send(
                data,
                `/api/team/${encodeURIComponent(m.id)}`,
                { version: m.version, role: f.get('role'), active: f.has('active') },
                'PUT',
              ),
            );
          }}
        >
          <strong>{m.name}</strong>
          <label className="field">
            <span>Papel de {m.name}</span>
            <select name="role" defaultValue={m.role} disabled={!data.permissions.admin}>
              {Object.entries(labels).map(([v, l]) => (
                <option value={v} key={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="check">
            <input
              type="checkbox"
              name="active"
              defaultChecked={m.active}
              disabled={!data.permissions.admin}
            />
            Acesso ativo
          </label>
          {data.permissions.admin && <button disabled={busy}>Salvar acesso</button>}
        </form>
      ))}
      {data.permissions.admin && data.mode === 'meta' && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void run(async () => {
              const r = await send(data, '/api/team/invites', {
                name: f.get('name'),
                role: f.get('role'),
              });
              setInvite(r.token);
            });
          }}
        >
          <h3>Convidar membro</h3>
          <label className="field">
            <span>Nome do convidado</span>
            <input required name="name" maxLength={250} />
          </label>
          <label className="field">
            <span>Papel inicial</span>
            <select name="role">
              <option value="editor">Editor</option>
              <option value="approver">Aprovador</option>
              <option value="reader">Leitor</option>
            </select>
          </label>
          <button disabled={busy}>Gerar convite</button>
          <p className="fine-print">
            Válido por uma hora. O perfil também precisa ter acesso ao piloto interno do app Meta.
          </p>
          {invite && (
            <label className="field">
              <span>Convite gerado · compartilhe diretamente com o membro</span>
              <input readOnly value={invite} onFocus={(e) => e.currentTarget.select()} />
            </label>
          )}
        </form>
      )}
      {data.permissions.admin && data.mode !== 'local' && <EmailInvites data={data} />}
    </section>
  );
}
export function CollectionSettings({ campaignId, data }: { campaignId: string; data: State }) {
  const [plan, setPlan] = useState<CollectionPlan>(),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  useEffect(() => {
    let active = true;
    fetch(`/api/campaigns/${encodeURIComponent(campaignId)}/collection`)
      .then(async (r) => {
        const v = await r.json();
        if (!r.ok) throw new Error(v.error);
        if (active) setPlan(v);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [campaignId]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!plan) return;
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError('');
    try {
      setPlan(
        await send(
          data,
          `/api/campaigns/${encodeURIComponent(campaignId)}/collection`,
          {
            version: plan.version,
            enabled: f.has('enabled'),
            slots: String(f.get('slots'))
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean),
          },
          'PUT',
        ),
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="panel collection-settings">
      <h2>Atualização das métricas</h2>
      <p>
        Consulta as publicações vinculadas nas contas conectadas. Horários de São Paulo; funciona
        enquanto o servidor está ligado e a campanha está ativa.
      </p>
      {error && (
        <p role="alert" className="notice error">
          {error}
        </p>
      )}
      {plan && (
        <form onSubmit={save} key={`${plan.id}:${plan.version}`}>
          <label className="check">
            <input
              name="enabled"
              type="checkbox"
              defaultChecked={plan.enabled}
              disabled={!data.permissions.admin || data.mode === 'local'}
            />
            Atualizar automaticamente
          </label>
          <label className="field">
            <span>Horários de coleta</span>
            <input
              name="slots"
              defaultValue={plan.slots.join(', ')}
              placeholder="09:00, 18:00"
              disabled={!data.permissions.admin}
            />
            <small>Separados por vírgula, formato HH:MM.</small>
          </label>
          {data.permissions.admin && (
            <button disabled={busy || data.mode === 'local'}>Salvar coleta</button>
          )}
          <p className="fine-print">
            Última coleta concluída:{' '}
            {plan.lastSuccess
              ? new Date(plan.lastSuccess).toLocaleString('pt-BR', {
                  timeZone: 'America/Sao_Paulo',
                })
              : 'Ainda não realizada'}
            .
          </p>
          {plan.lastError && <p className="notice error">{plan.lastError}</p>}
          {data.mode === 'local' && (
            <p className="fine-print">
              Ative no servidor com conexão Meta. As leituras acumuladas mantêm sua data e não
              equivalem automaticamente a uma janela de sete dias.
            </p>
          )}
        </form>
      )}
      <CollectionHistory campaignId={campaignId} revision={plan?.version} data={data} />
    </section>
  );
}
